import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Shield, Laptop, Globe, BookOpen, Users } from '../utils/icons';
import { FaLinkedinIn } from 'react-icons/fa';
import { useI18n } from '../i18n';

const Services = () => {
  const { t } = useI18n();

  const services = [
    {
      code: 'SRV-01',
      title: t('servicesPage.items.securityAssessment.title'),
      description: t('servicesPage.items.securityAssessment.desc'),
      icon: Shield,
      status: t('servicesPage.status.available'),
      deliverable: t('servicesPage.deliverables.securityAssessment'),
    },
    {
      code: 'SRV-02',
      title: t('servicesPage.items.webHardening.title'),
      description: t('servicesPage.items.webHardening.desc'),
      icon: Globe,
      status: t('servicesPage.status.available'),
      deliverable: t('servicesPage.deliverables.webHardening'),
    },
    {
      code: 'SRV-03',
      title: t('servicesPage.items.mobileReview.title'),
      description: t('servicesPage.items.mobileReview.desc'),
      icon: Laptop,
      status: t('servicesPage.status.available'),
      deliverable: t('servicesPage.deliverables.mobileReview'),
    },
    {
      code: 'SRV-04',
      title: t('servicesPage.items.incidentResponse.title'),
      description: t('servicesPage.items.incidentResponse.desc'),
      icon: ShieldCheck,
      status: t('servicesPage.status.onRequest'),
      deliverable: t('servicesPage.deliverables.incidentResponse'),
    },
    {
      code: 'SRV-05',
      title: t('servicesPage.items.training.title'),
      description: t('servicesPage.items.training.desc'),
      icon: BookOpen,
      status: t('servicesPage.status.available'),
      deliverable: t('servicesPage.deliverables.training'),
    },
    {
      code: 'SRV-06',
      title: t('servicesPage.items.consulting.title'),
      description: t('servicesPage.items.consulting.desc'),
      icon: Users,
      status: t('servicesPage.status.custom'),
      deliverable: t('servicesPage.deliverables.consulting'),
    },
  ];

  const approachValue = t('servicesPage.approach.items');
  const approach = Array.isArray(approachValue) ? approachValue : [];

  const fadeUp = {
    hidden: { opacity: 1, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <div className="pt-20 ccc-site min-h-screen">
      <section className="ccc-brief-hero">
        <div className="ccc-container py-16 md:py-20">
          <p className="ccc-eyebrow mb-5">{t('ui.securityServices')}</p>
          <h1 className="ccc-brief-title">{t('servicesPage.hero.title')}</h1>
          <p className="ccc-brief-copy mt-6 max-w-3xl">{t('servicesPage.hero.subtitle')}</p>
        </div>
      </section>

      <section className="ccc-section">
        <div className="ccc-container">
          <div className="ccc-section-head">
            <div>
              <p className="ccc-eyebrow mb-4">{t('ui.engagementMenu')}</p>
              <h2 className="ccc-section-title">{t('ui.chooseWorkTitle')}</h2>
            </div>
            <p className="ccc-section-copy">{t('ui.chooseWorkCopy')}</p>
          </div>

          <div className="ccc-row-list">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <motion.article
                  key={service.code}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  className="ccc-work-row"
                >
                  <div>
                    <p className="ccc-label">{service.code}</p>
                    <Icon className="mt-3 h-7 w-7 text-emerald-700" />
                  </div>
                  <div>
                    <h2 className="ccc-work-title">{service.title}</h2>
                    <span className="ccc-pill mt-3">{service.status}</span>
                  </div>
                  <p className="ccc-work-copy">{service.description}</p>
                  <div>
                    <p className="ccc-label">{t('ui.deliverable')}</p>
                    <p className="ccc-value mt-2">{service.deliverable}</p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ccc-section bg-[#e1e9e5]">
        <div className="ccc-container">
          <div className="ccc-section-head">
            <div>
              <p className="ccc-eyebrow mb-4">{t('ui.workflow')}</p>
              <h2 className="ccc-section-title">{t('servicesPage.approach.title')}</h2>
            </div>
            <p className="ccc-section-copy">{t('servicesPage.approach.subtitle')}</p>
          </div>
          <div className="ccc-scope-grid">
            {approach.map((step, index) => (
              <article key={step.title} className="ccc-scope-cell">
                <p className="ccc-scope-number">{String(index + 1).padStart(2, '0')}</p>
                <h3 className="ccc-scope-title">{step.title}</h3>
                <p className="ccc-scope-copy">{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ccc-section-tight">
        <div className="ccc-container">
          <div className="ccc-ledger-dark p-6 md:p-8">
            <div className="grid gap-7 md:grid-cols-[minmax(0,0.8fr)_auto] md:items-center">
              <div>
                <p className="ccc-label mb-3">{t('ui.startHere')}</p>
                <h2 className="text-3xl font-black md:text-5xl">{t('servicesPage.cta.title')}</h2>
                <p className="mt-4 max-w-2xl text-slate-300">{t('servicesPage.cta.subtitle')}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <a
                  href="https://www.linkedin.com/in/miguel083"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ccc-primary-btn px-5 py-3"
                >
                  <FaLinkedinIn className="h-5 w-5" />
                  {t('servicesPage.cta.linkedin')}
                </a>
                <a href="mailto:info@crespocyberconsulting.com" className="ccc-secondary-btn px-5 py-3">
                  {t('servicesPage.cta.contact')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
